import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import PageTitle from '../../components/shared/PageTitle';

// "Connect your agent" guide (docs/MCP_SERVER_PLAN.md §9). The MCP endpoint lives at the
// API host root (/mcp), NOT under /api — derive it by stripping the /api suffix.
const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
const MCP_URL = `${apiBase.replace(/\/api\/?$/, '')}/mcp`;

const cursorSnippet = `{
  "mcpServers": {
    "clareefai": {
      "url": "${MCP_URL}"
    }
  }
}`;

function CopyRow({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 truncate rounded bg-gray-50 px-3 py-2 text-xs text-gray-700 border border-gray-200">{value}</code>
      <button onClick={copy} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        {copied ? t('agentAccess.tokens.copied') : t('agentAccess.tokens.copy')}
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-3 text-sm text-gray-600">{children}</div>
    </div>
  );
}

export default function ConnectAgentGuide() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button onClick={() => navigate('/settings/agent-access')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="h-4 w-4" /> {t('agentAccess.guide.back')}
      </button>

      <PageTitle title={t('agentAccess.guide.title')} />
      <h1 className="text-xl font-semibold text-gray-900">{t('agentAccess.guide.title')}</h1>
      <p className="text-sm text-gray-500 mb-6">{t('agentAccess.guide.subtitle')}</p>

      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">{t('agentAccess.guide.serverUrl')}</p>
        <CopyRow value={MCP_URL} />
      </div>

      <p className="mb-6 rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
        {t('agentAccess.guide.writeNote')}
      </p>

      <div className="space-y-4">
        <Section title={t('agentAccess.guide.claude.title')}>
          <p>{t('agentAccess.guide.claude.body')}</p>
        </Section>

        <Section title={t('agentAccess.guide.chatgpt.title')}>
          <p>{t('agentAccess.guide.chatgpt.body')}</p>
          <p className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            {t('agentAccess.guide.chatgpt.devMode')}
          </p>
        </Section>

        <Section title={t('agentAccess.guide.cursor.title')}>
          <p>{t('agentAccess.guide.cursor.body')}</p>
          <pre className="overflow-x-auto rounded bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700">{cursorSnippet}</pre>
        </Section>

        <Section title={t('agentAccess.guide.headless.title')}>
          <p>{t('agentAccess.guide.headless.body')}</p>
          <button onClick={() => navigate('/settings/agent-access')} className="text-sm font-medium text-purple-700 underline">
            {t('agentAccess.guide.headless.cta')}
          </button>
        </Section>
      </div>
    </div>
  );
}
